import { useEffect, useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { LargeTitleHeader } from '@/components/common';
import apiClient from '@/lib/api/client';
import { PublicSiteQueue } from '@/components/dev/PublicSiteQueue';

// ── Marketing-site analytics ────────────────────────────────────────────────
// Read-only view of ochrepoint.ca traffic, replacing the legacy
// admin.ochrepoint.ca dashboard. Data still collects through the legacy
// tracker; this reads its database directly.

interface Overview {
  totals: { visitors: number; sessions: number; pageviews: number; sessions_7d: number; sessions_30d: number };
  daily: { day: string; sessions: number }[];
  pages: { page: string; views: number }[];
  referrals: RefLink[];
}
interface SiteError {
  message: string; level: string; count: number; last_seen: string;
  page: string; browsers: string[]; resolved: boolean;
}
interface RefLink {
  id: string; slug: string; name: string; destination: string;
  utm_source?: string; utm_campaign?: string; active: number;
  clicks: number; clicks_7d: number;
}

const Tile = ({ label, value, hint }: { label: string; value: number | string; hint?: string }) => (
  <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
    <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">{label}</p>
    <p className="text-2xl font-bold text-gray-900">{typeof value === 'number' ? value.toLocaleString('en-CA') : value}</p>
    {hint && <p className="text-[11px] text-gray-400">{hint}</p>}
  </div>
);

export const SiteAnalyticsPage = () => {
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [fName, setFName] = useState('');
  const [fSlug, setFSlug] = useState('');
  const [fDest, setFDest] = useState('https://ochrepoint.ca/');
  const [fCampaign, setFCampaign] = useState('');
  const [refMsg, setRefMsg] = useState('');
  const [copiedSlug, setCopiedSlug] = useState('');
  const [errors, setErrors] = useState<SiteError[]>([]);
  const [showResolved, setShowResolved] = useState(false);

  const load = () =>
    apiClient.get('/site-analytics/overview')
      .then(r => setData(r.data.data))
      .catch(e => setError(e?.response?.data?.error || 'Could not reach the analytics database.'));
  const loadErrors = () =>
    apiClient.get('/site-analytics/errors').then(r => setErrors(r.data.data.errors ?? [])).catch(() => {});
  useEffect(() => { load(); loadErrors(); }, []);

  const [resolveNote, setResolveNote] = useState('');
  const [queueingAll, setQueueingAll] = useState(false);
  const resolveError = async (e: SiteError) => {
    await apiClient.post('/site-analytics/errors/resolve', {
      message_pattern: e.message.slice(0, 200), level: e.level,
      count: e.count, browsers: e.browsers.join(', '), page: e.page,
    }).catch(() => {});
    setResolveNote('Queued as a change request — it will be audited and fixed.');
    setTimeout(() => setResolveNote(''), 6000);
    loadErrors();
  };

  // CR #126: file every outstanding error at once (sequential — each POST also
  // creates a change_request, and the box is a 1-vCPU VPS).
  const queueAll = async () => {
    const pending = errors.filter(e => !e.resolved);
    if (pending.length === 0) return;
    setQueueingAll(true);
    for (const e of pending) {
      await apiClient.post('/site-analytics/errors/resolve', {
        message_pattern: e.message.slice(0, 200), level: e.level,
        count: e.count, browsers: e.browsers.join(', '), page: e.page,
      }).catch(() => {});
    }
    setQueueingAll(false);
    setResolveNote(`Queued ${pending.length} error${pending.length === 1 ? '' : 's'} for audit & fix.`);
    setTimeout(() => setResolveNote(''), 8000);
    loadErrors();
  };

  const addLink = async () => {
    setRefMsg('');
    try {
      await apiClient.post('/site-analytics/referral-links', {
        name: fName, slug: fSlug, destination: fDest, utm_campaign: fCampaign,
      });
      setAdding(false); setFName(''); setFSlug(''); setFCampaign('');
      load();
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      setRefMsg(err.response?.data?.error || 'Could not create link');
    }
  };

  const toggleActive = async (l: RefLink) => {
    await apiClient.put(`/site-analytics/referral-links/${l.id}`, { active: !l.active }).catch(() => {});
    load();
  };

  const removeLink = async (l: RefLink) => {
    if (!window.confirm(`Delete "${l.name}" and its ${l.clicks} click records?`)) return;
    await apiClient.delete(`/site-analytics/referral-links/${l.id}`).catch(() => {});
    load();
  };

  const copyUrl = (l: RefLink) => {
    navigator.clipboard.writeText(`https://ops.ochrepoint.ca/r/${l.slug}`).then(() => {
      setCopiedSlug(l.slug);
      setTimeout(() => setCopiedSlug(''), 2000);
    });
  };

  const maxDaily = Math.max(1, ...(data?.daily.map(d => d.sessions) ?? [1]));
  const maxViews = Math.max(1, ...(data?.pages.map(p => p.views) ?? [1]));

  return (
    <MainLayout>
      <LargeTitleHeader
        title="Site Analytics"
        subtitle="ochrepoint.ca traffic — visitors, top pages, and referral links"
      />

      {error ? (
        <div className="bg-white rounded-xl border border-red-200 p-6 text-sm text-red-700">{error}</div>
      ) : !data ? (
        <div className="h-24 animate-pulse rounded-xl bg-gray-100" />
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
            <Tile label="Visitors" value={data.totals.visitors} hint="all time" />
            <Tile label="Sessions" value={data.totals.sessions} hint="all time" />
            <Tile label="Pageviews" value={data.totals.pageviews} hint="all time" />
            <Tile label="Sessions · 7d" value={data.totals.sessions_7d} />
            <Tile label="Sessions · 30d" value={data.totals.sessions_30d} />
          </div>

          {/* Daily sessions, last 30 days */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-2">Sessions — last 30 days</p>
            {data.daily.length === 0 ? (
              <p className="text-sm text-gray-400">No sessions recorded in the last 30 days.</p>
            ) : (
              <div className="flex items-end gap-[3px] h-24">
                {data.daily.map(d => (
                  <div key={d.day} className="flex-1 flex flex-col items-center group relative">
                    <div className="w-full rounded-t-[3px] bg-[#C77D3F]"
                      style={{ height: `${Math.max(4, (d.sessions / maxDaily) * 88)}px` }} />
                    <div className="absolute -top-7 hidden group-hover:block bg-[#2C1810] text-white text-[10px] rounded px-1.5 py-0.5 whitespace-nowrap z-10">
                      {new Date(d.day + 'T12:00:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })} · {d.sessions}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            {/* Top pages */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-2">Top pages — 30 days</p>
              {data.pages.length === 0 ? (
                <p className="text-sm text-gray-400">No pageviews in the last 30 days.</p>
              ) : data.pages.map(p => (
                <div key={p.page} className="flex items-center gap-2 py-1">
                  <span className="text-xs text-gray-700 w-40 truncate" title={p.page}>{p.page || '/'}</span>
                  <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-[#5C7F4F]" style={{ width: `${(p.views / maxViews) * 100}%` }} />
                  </div>
                  <span className="text-xs font-semibold text-gray-900 w-10 text-right">{p.views}</span>
                </div>
              ))}
            </div>

            {/* Referral links manager */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">Referral links</p>
                <button onClick={() => setAdding(a => !a)}
                  className="h-7 px-2.5 rounded-lg bg-[#C77D3F] text-white text-[11px] font-bold">
                  {adding ? 'Cancel' : '+ New link'}
                </button>
              </div>
              {adding && (
                <div className="border border-[#C77D3F]/30 rounded-lg p-2.5 mb-2 space-y-1.5">
                  <div className="flex gap-1.5">
                    <input value={fName} onChange={e => setFName(e.target.value)} placeholder="Name (e.g. Spring flyer)"
                      className="flex-1 border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none" />
                    <input value={fSlug} onChange={e => setFSlug(e.target.value)} placeholder="slug"
                      className="w-24 border border-gray-300 rounded-lg px-2 py-1.5 text-xs font-mono focus:outline-none" />
                  </div>
                  <div className="flex gap-1.5">
                    <input value={fDest} onChange={e => setFDest(e.target.value)} placeholder="Destination URL"
                      className="flex-1 border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none" />
                    <input value={fCampaign} onChange={e => setFCampaign(e.target.value)} placeholder="Campaign (opt.)"
                      className="w-28 border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none" />
                  </div>
                  {refMsg && <p className="text-[11px] text-red-600">{refMsg}</p>}
                  <button onClick={addLink} disabled={!fName.trim() || !fSlug.trim()}
                    className="h-8 px-3 rounded-lg bg-[#2C1810] text-white text-xs font-bold disabled:opacity-40">Create</button>
                </div>
              )}
              {data.referrals.length === 0 ? (
                <p className="text-sm text-gray-400">No referral links yet — create one to track a flyer, post, or ad.</p>
              ) : data.referrals.map(r => (
                <div key={r.slug} className={`flex items-center gap-1.5 py-1 text-xs ${r.active ? '' : 'opacity-45'}`}>
                  <span className="text-gray-700 truncate flex-1 min-w-0">
                    <span className="font-semibold">{r.name}</span>
                    <code className="text-gray-400 ml-1.5">/r/{r.slug}</code>
                  </span>
                  <span className="font-bold text-gray-900 whitespace-nowrap">{r.clicks}</span>
                  {Number(r.clicks_7d) > 0 && <span className="text-green-700 whitespace-nowrap">+{r.clicks_7d} wk</span>}
                  <button onClick={() => copyUrl(r)} title="Copy link URL"
                    className={copiedSlug === r.slug ? 'text-green-600' : 'text-gray-300 hover:text-[#C77D3F]'}>
                    <span className="material-symbols-outlined text-base">{copiedSlug === r.slug ? 'check' : 'content_copy'}</span>
                  </button>
                  <button onClick={() => toggleActive(r)} title={r.active ? 'Deactivate' : 'Activate'}
                    className="text-gray-300 hover:text-amber-600">
                    <span className="material-symbols-outlined text-base">{r.active ? 'toggle_on' : 'toggle_off'}</span>
                  </button>
                  <button onClick={() => removeLink(r)} title="Delete link" className="text-gray-300 hover:text-red-600">
                    <span className="material-symbols-outlined text-base">delete</span>
                  </button>
                </div>
              ))}
              <p className="text-[10px] text-gray-400 mt-2">Share as <code>ops.ochrepoint.ca/r/&lt;slug&gt;</code> — clicks are tagged with UTM and land on the destination. Old <code>admin.ochrepoint.ca/r/…</code> links keep working until decommission.</p>
            </div>
          </div>

          {/* JS error log from the marketing site */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">Site errors — last 30 days</p>
              {resolveNote && <p className="text-[11px] text-green-700">{resolveNote}</p>}
              <div className="flex items-center gap-2 ml-auto">
                {/* CR #126: queue every outstanding error for audit in one go */}
                {errors.some(e => !e.resolved) && (
                  <button onClick={queueAll} disabled={queueingAll}
                    className="h-7 px-2.5 rounded-md border border-[#C77D3F]/40 text-[11px] font-bold text-[#C77D3F] hover:bg-[#C77D3F]/5 disabled:opacity-50">
                    {queueingAll ? 'Queueing…' : `Queue all for audit (${errors.filter(e => !e.resolved).length})`}
                  </button>
                )}
                <label className="flex items-center gap-1.5 text-[11px] text-gray-500">
                  <input type="checkbox" checked={showResolved} onChange={e => setShowResolved(e.target.checked)} className="accent-[#C77D3F]" />
                  Show queued
                </label>
              </div>
            </div>
            {errors.filter(e => showResolved || !e.resolved).length === 0 ? (
              <p className="text-sm text-gray-400">No unresolved JavaScript errors on ochrepoint.ca. 🎉</p>
            ) : errors.filter(e => showResolved || !e.resolved).map((e, i) => (
              <div key={i} className={`flex items-start gap-2 py-1.5 border-t border-gray-50 first:border-0 ${e.resolved ? 'opacity-45' : ''}`}>
                <span className={`shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full mt-0.5 ${e.level === 'error' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'}`}>
                  {e.level} ×{e.count}
                </span>
                <span className="flex-1 min-w-0 text-xs text-gray-700 break-words">{e.message}
                  {e.browsers.length > 0 && <span className="text-gray-400"> · {e.browsers.join(', ')}</span>}
                </span>
                {e.resolved ? (
                  <span className="shrink-0 text-[10px] font-bold text-green-700 bg-green-50 rounded-md px-2 py-0.5">Queued</span>
                ) : (
                  <button onClick={() => resolveError(e)}
                    title="File this error into the change-request queue so it gets audited and fixed"
                    className="shrink-0 h-6 px-2 rounded-md border border-gray-200 text-[10px] font-bold text-gray-500 hover:border-[#C77D3F] hover:text-[#C77D3F]">
                    Queue audit &amp; fix
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CR #149: file and track update/change/audit work against the public
          marketing site — the same queue the errors panel feeds. Rendered
          outside the analytics-data branch so it still works if the tracker
          database is unreachable. */}
      <div className="mt-4">
        <PublicSiteQueue />
      </div>

      {/* CR #122: the WHOLE tracker database, surfaced */}
      <DeepDive />
    </MainLayout>
  );
};

// ── Deep dive (CR #122) — everything the tracker records ────────────────────
interface KV { k: string; n: number }
interface Visit {
  started_at: string; duration: number; page_count: number; landing_page: string; source: string;
  device_type: string; browser_name: string; browser_version?: string; os_name: string;
  geo_city: string; geo_country: string; visit_count: number;
  ip_address?: string | null; is_bot?: number; bot_score?: number; is_vpn?: number; is_proxy?: number;
}
interface Detail {
  audience: { visitors: number; returning_visitors: number; bots: number; vpn_or_proxy: number; ad_blockers: number; prefers_dark: number };
  engagement: { sessions: number; avg_duration_s: number | null; avg_pages: number | null; bounce_pct: number | null; avg_scroll_pct: number | null };
  devices: KV[]; browsers: KV[]; oses: KV[]; places: KV[];
  sources: KV[]; utm: KV[]; landings: KV[];
  eventTypes: KV[]; sections: KV[]; popups: KV[]; clicks: KV[];
  recentSessions: Visit[];
  recentAll?: Visit[];
}

const fmtDur = (s: number | null | undefined) => {
  const n = Number(s) || 0;
  if (!n) return '—';
  if (n < 90) return `${n}s`;
  return `${Math.round(n / 60)}m`;
};

function BarList({ title, rows, icon }: { title: string; rows: KV[]; icon: string }) {
  const max = Math.max(1, ...rows.map(r => Number(r.n)));
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <h3 className="text-sm font-bold text-gray-700 mb-2.5 flex items-center gap-1.5">
        <span className="material-symbols-outlined text-base text-gray-400">{icon}</span>{title}
      </h3>
      {rows.length === 0 ? <p className="text-xs text-gray-400">No data in this range.</p> : (
        <div className="space-y-1.5">
          {rows.map(r => (
            <div key={r.k} className="flex items-center gap-2 text-xs">
              <span className="w-32 truncate text-gray-600" title={r.k}>{r.k}</span>
              <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                <div className="h-full bg-[#C77D3F]/70 rounded-full" style={{ width: `${(Number(r.n) / max) * 100}%` }} />
              </div>
              <span className="w-10 text-right font-semibold text-gray-800">{Number(r.n).toLocaleString('en-CA')}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DeepDive() {
  const [range, setRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d');
  const [showBots, setShowBots] = useState(false); // CR #148
  const [d, setD] = useState<Detail | null>(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    setD(null);
    apiClient.get(`/site-analytics/detail?range=${range}`)
      .then(r => setD(r.data.data))
      .catch(e => setErr(e?.response?.data?.error || 'Could not load the deep dive.'));
  }, [range]);

  return (
    <div className="space-y-4 pt-2">
      <div className="flex items-center justify-between flex-wrap gap-2 border-t border-gray-200 pt-5">
        <h2 className="text-lg font-bold text-gray-900">Deep dive — everything the tracker sees</h2>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {(['7d', '30d', '90d', 'all'] as const).map(r => (
            <button key={r} onClick={() => setRange(r)}
              className={`px-3 py-1 text-xs font-bold rounded-md ${range === r ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>
              {r === 'all' ? 'All time' : r}
            </button>
          ))}
        </div>
      </div>

      {err && <p className="text-sm text-red-600">{err}</p>}
      {!d && !err && <div className="h-40 rounded-xl bg-gray-100 animate-pulse" />}

      {d && (
        <>
          {/* Audience + engagement tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <Tile label="Human visitors" value={Number(d.audience.visitors)} />
            <Tile label="Returning" value={Number(d.audience.returning_visitors) || 0} />
            <Tile label="Bots filtered" value={Number(d.audience.bots) || 0} />
            <Tile label="VPN / proxy" value={Number(d.audience.vpn_or_proxy) || 0} />
            <Tile label="Avg session" value={fmtDur(d.engagement.avg_duration_s)} />
            <Tile label="Bounce rate" value={d.engagement.bounce_pct != null ? `${d.engagement.bounce_pct}%` : '—'} hint={`${Number(d.engagement.avg_pages) || 0} pages/session`} />
          </div>

          {/* Audience breakdowns */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <BarList title="Devices" rows={d.devices} icon="devices" />
            <BarList title="Browsers" rows={d.browsers} icon="public" />
            <BarList title="Operating systems" rows={d.oses} icon="memory" />
            <BarList title="Where visitors are" rows={d.places} icon="location_on" />
          </div>

          {/* Acquisition */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <BarList title="Traffic sources" rows={d.sources} icon="alt_route" />
            <BarList title="UTM campaigns" rows={d.utm} icon="campaign" />
            <BarList title="Landing pages" rows={d.landings} icon="flight_land" />
          </div>

          {/* On-page behaviour */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <BarList title="Sections viewed" rows={d.sections} icon="view_agenda" />
            <BarList title="Popups opened" rows={d.popups} icon="open_in_new" />
            <BarList title="What gets clicked" rows={d.clicks} icon="ads_click" />
            <BarList title="Event volume" rows={d.eventTypes} icon="monitoring" />
          </div>

          {/* Recent sessions */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2">
              <h3 className="text-sm font-bold text-gray-700">Recent visits</h3>
              <label className="ml-auto flex items-center gap-1.5 text-[11px] text-gray-500">
                <input type="checkbox" checked={showBots} onChange={e => setShowBots(e.target.checked)} className="accent-[#C77D3F]" />
                Include bots
              </label>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[720px]">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wide text-gray-400 border-b border-gray-100 bg-gray-50">
                    <th className="px-4 py-2 font-semibold">When</th>
                    <th className="px-2 py-2 font-semibold">From</th>
                    <th className="px-2 py-2 font-semibold">Source</th>
                    <th className="px-2 py-2 font-semibold">Device</th>
                    <th className="px-2 py-2 font-semibold">Browser</th>
                    <th className="px-2 py-2 font-semibold">IP</th>
                    <th className="px-2 py-2 font-semibold">Human</th>
                    <th className="px-2 py-2 font-semibold">Landed on</th>
                    <th className="px-2 py-2 font-semibold text-right">Pages</th>
                    <th className="px-4 py-2 font-semibold text-right">Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {(showBots ? (d.recentAll || d.recentSessions) : d.recentSessions).map((s, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-gray-700 whitespace-nowrap">
                        {new Date(s.started_at).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        {s.visit_count > 1 && <span className="ml-1 text-[10px] text-primary font-bold">×{s.visit_count}</span>}
                      </td>
                      <td className="px-2 py-2 text-gray-500 whitespace-nowrap">{[s.geo_city, s.geo_country].filter(Boolean).join(', ') || '—'}</td>
                      <td className="px-2 py-2 text-gray-500">{s.source}</td>
                      <td className="px-2 py-2 text-gray-500 capitalize">{s.device_type || '—'}</td>
                      <td className="px-2 py-2 text-gray-500 whitespace-nowrap">
                        {[s.browser_name, s.browser_version?.split('.')[0]].filter(Boolean).join(' ') || '—'}
                      </td>
                      <td className="px-2 py-2 text-gray-500 font-mono text-[10px] whitespace-nowrap">
                        {s.ip_address || '—'}
                        {(s.is_vpn || s.is_proxy) ? <span className="ml-1 text-amber-600 font-sans font-bold">VPN</span> : null}
                      </td>
                      <td className="px-2 py-2">
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                          s.is_bot ? 'bg-gray-200 text-gray-600' : 'bg-green-100 text-green-700'}`}
                          title={s.is_bot ? `bot score ${s.bot_score ?? ''}` : 'passed bot checks'}>
                          {s.is_bot ? 'No' : 'Yes'}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-gray-500 max-w-[160px] truncate">{s.landing_page || '/'}</td>
                      <td className="px-2 py-2 text-right text-gray-700">{s.page_count}</td>
                      <td className="px-4 py-2 text-right text-gray-700">{fmtDur(s.duration)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
